import katex from "katex";
import "katex/dist/katex.min.css";
// Import mhchem extension for chemistry support
import "katex/contrib/mhchem";

// Lilia's maths shortcuts, used until the server's list for the document arrives (GET
// …/preview/math-macros, API #343). The server's list is the one both the editor and the PDF use:
// these shortcuts, KaTeX stand-ins for siunitx, and the document's own preamble macros.
export const physicsMacros: Record<string, string> = {
  // Bra-ket notation (Dirac notation)
  "\\bra": "\\langle #1 |",
  "\\ket": "| #1 \\rangle",
  "\\braket": "\\langle #1 | #2 \\rangle",
  "\\Bra": "\\left\\langle #1 \\right|",
  "\\Ket": "\\left| #1 \\right\\rangle",
  "\\Braket": "\\left\\langle #1 \\middle| #2 \\right\\rangle",

  // Expectation value and matrix elements
  "\\ev": "\\langle #1 \\rangle",
  "\\Ev": "\\left\\langle #1 \\right\\rangle",
  "\\mel": "\\langle #1 | #2 | #3 \\rangle",

  // Commutators and anti-commutators
  "\\comm": "\\left[ #1, #2 \\right]",
  "\\acomm": "\\left\\{ #1, #2 \\right\\}",
  "\\pb": "\\left\\{ #1, #2 \\right\\}",

  // Differentials
  "\\dd": "\\mathrm{d}#1",
  "\\dv": "\\frac{\\mathrm{d} #1}{\\mathrm{d} #2}",
  "\\pdv": "\\frac{\\partial #1}{\\partial #2}",
  "\\fdv": "\\frac{\\delta #1}{\\delta #2}",

  // Vector calculus
  "\\grad": "\\nabla",
  "\\divergence": "\\nabla \\cdot",
  "\\curl": "\\nabla \\times",
  "\\laplacian": "\\nabla^2",

  // Vector notation
  "\\vb": "\\mathbf{#1}",
  "\\va": "\\vec{\\mathbf{#1}}",
  "\\vu": "\\hat{\\mathbf{#1}}",
  "\\vdot": "\\boldsymbol{\\cdot}",
  "\\cross": "\\boldsymbol{\\times}",

  // Quantum mechanics. \hbar, \Re and \Im are LaTeX's own and print as LaTeX prints them
  // (ħ, ℜ, ℑ): redefining them here made the editor disagree with the PDF (18 Oct).
  // Note: \psi and \Psi are native KaTeX symbols — do not redefine (causes circular refs)

  // Common physics operators
  "\\op": "\\hat{#1}",
  "\\abs": "\\left| #1 \\right|",
  "\\norm": "\\left\\| #1 \\right\\|",
  "\\order": "\\mathcal{O}\\left( #1 \\right)",

  // Trigonometric and hyperbolic functions (improved)
  "\\asin": "\\sin^{-1}",
  "\\acos": "\\cos^{-1}",
  "\\atan": "\\tan^{-1}",
  "\\acsc": "\\csc^{-1}",
  "\\asec": "\\sec^{-1}",
  "\\acot": "\\cot^{-1}",

  // Trace and determinant
  "\\Tr": "\\operatorname{Tr}",
  "\\tr": "\\operatorname{tr}",
  "\\rank": "\\operatorname{rank}",

  // Common matrices
  "\\identity": "\\mathbb{1}",
  "\\paulix": "\\sigma_x",
  "\\pauliy": "\\sigma_y",
  "\\pauliz": "\\sigma_z",
};

export interface KaTeXRenderOptions {
  displayMode?: boolean;
  throwOnError?: boolean;
  errorColor?: string;
  macros?: Record<string, string>;
  strict?: boolean | string;
  trust?: boolean;
  output?: "html" | "mathml" | "htmlAndMathml";
}

// ── The document's macros (one set for the editor and the PDF, 18 Oct) ──────────────────────────
let serverMacros: Record<string, string> | null = null;
let macrosVersion = 0;
const macroListeners = new Set<() => void>();

/** Fired on window when the macros change: maths drawn outside React re-renders on it. */
export const MATH_MACROS_EVENT = "lilia:math-macros";

/** The document's macros from the server, or null for Lilia's shortcuts alone. */
export function setMathMacros(macros: Record<string, string> | null): void {
  serverMacros = macros;
  macrosVersion += 1;
  macroListeners.forEach((l) => l());
  if (typeof window !== "undefined") window.dispatchEvent(new Event(MATH_MACROS_EVENT));
}

export function subscribeMathMacros(listener: () => void): () => void {
  macroListeners.add(listener);
  return () => macroListeners.delete(listener);
}

export function mathMacrosVersion(): number {
  return macrosVersion;
}

/** A fresh copy each render: KaTeX writes \gdef definitions into the object it is given. */
export function currentMathMacros(): Record<string, string> {
  return { ...(serverMacros ?? physicsMacros) };
}

const defaultOptions: KaTeXRenderOptions = {
  displayMode: true,
  throwOnError: false,
  errorColor: "#cc0000",
  strict: false,
  trust: true,
  output: "htmlAndMathml",
};

/**
 * LaTeX KaTeX cannot parse but can show a close stand-in for. KaTeX has no
 * multline: it is shown as gather (lines centred rather than stepped), as
 * the equation block's own "multline" mode already is — not as an error.
 * KaTeX has no \label either: an align imported with a label on each line
 * keeps them in its LaTeX (so \eqref to each resolves in the PDF), and they
 * are left out of the display.
 * The display only; the LaTeX itself is never rewritten.
 */
function forKatex(latex: string): string {
  return latex
    .replace(/\\(begin|end)\{multline(\*?)\}/g, "\\$1{gather$2}")
    .replace(/\\label\{[^{}]*\}/g, "");
}

/**
 * Render LaTeX to HTML string
 */
export function renderLatex(latex: string, options?: Partial<KaTeXRenderOptions>): string {
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    macros: {
      ...currentMathMacros(),
      ...options?.macros,
    },
  };

  try {
    return katex.renderToString(forKatex(latex), mergedOptions as Parameters<typeof katex.renderToString>[1]);
  } catch (error) {
    if (mergedOptions.throwOnError) {
      throw error;
    }
    // Return error message in HTML
    return `<span class="katex-error" style="color: ${mergedOptions.errorColor}">Error: ${
      error instanceof Error ? error.message : "Unknown error"
    }</span>`;
  }
}

/**
 * Render LaTeX directly to a DOM element
 */
export function renderLatexToElement(
  latex: string,
  element: HTMLElement,
  options?: Partial<KaTeXRenderOptions>
): void {
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    macros: {
      ...currentMathMacros(),
      ...options?.macros,
    },
  };

  try {
    katex.render(forKatex(latex), element, mergedOptions as Parameters<typeof katex.render>[2]);
  } catch (error) {
    if (mergedOptions.throwOnError) {
      throw error;
    }
    element.innerHTML = `<span class="katex-error" style="color: ${mergedOptions.errorColor}">Error: ${
      error instanceof Error ? error.message : "Unknown error"
    }</span>`;
  }
}

/**
 * Check if LaTeX is valid
 */
export function isValidLatex(latex: string): { valid: boolean; error?: string } {
  try {
    katex.renderToString(forKatex(latex), {
      ...defaultOptions,
      macros: currentMathMacros(),
      throwOnError: true,
    } as Parameters<typeof katex.renderToString>[1]);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Symbol categories for the equation toolbar
 */
export type SymbolCategory = "math" | "greek" | "physics" | "chemistry" | "symbols" | "operators";

export interface SymbolButton {
  label: string;
  latex: string;
  preview?: string;
  category: SymbolCategory;
  tooltip?: string;
}

export const EQUATION_SYMBOLS: SymbolButton[] = [
  // Math - Basic
  { label: "Fraction", latex: "\\frac{▢}{▢}", category: "math", tooltip: "Fraction" },
  { label: "√", latex: "\\sqrt{▢}", category: "math", tooltip: "Square root" },
  { label: "ⁿ√", latex: "\\sqrt[▢]{▢}", category: "math", tooltip: "Nth root" },
  { label: "xⁿ", latex: "▢^{▢}", category: "math", tooltip: "Superscript/power" },
  { label: "xₙ", latex: "▢_{▢}", category: "math", tooltip: "Subscript" },
  { label: "∫", latex: "\\int_{▢}^{▢}", category: "math", tooltip: "Integral" },
  { label: "∬", latex: "\\iint", category: "math", tooltip: "Double integral" },
  { label: "∮", latex: "\\oint", category: "math", tooltip: "Contour integral" },
  { label: "∑", latex: "\\sum_{▢}^{▢}", category: "math", tooltip: "Sum" },
  { label: "∏", latex: "\\prod_{▢}^{▢}", category: "math", tooltip: "Product" },
  { label: "lim", latex: "\\lim_{▢ \\to ▢}", category: "math", tooltip: "Limit" },
  { label: "()", latex: "\\left( ▢ \\right)", category: "math", tooltip: "Parentheses" },
  { label: "[]", latex: "\\left[ ▢ \\right]", category: "math", tooltip: "Brackets" },
  { label: "{}", latex: "\\left\\{ ▢ \\right\\}", category: "math", tooltip: "Braces" },
  { label: "||", latex: "\\left| ▢ \\right|", category: "math", tooltip: "Absolute value" },

  // Math - Matrices
  { label: "Matrix", latex: "\\begin{pmatrix} ▢ & ▢ \\\\ ▢ & ▢ \\end{pmatrix}", category: "math", tooltip: "2x2 matrix" },
  { label: "Det", latex: "\\begin{vmatrix} ▢ & ▢ \\\\ ▢ & ▢ \\end{vmatrix}", category: "math", tooltip: "Determinant" },
  { label: "Cases", latex: "\\begin{cases} ▢ & \\text{if } ▢ \\\\ ▢ & \\text{if } ▢ \\end{cases}", category: "math", tooltip: "Piecewise function" },

  // Greek Letters - Lowercase
  { label: "α", latex: "\\alpha", category: "greek", tooltip: "alpha" },
  { label: "β", latex: "\\beta", category: "greek", tooltip: "beta" },
  { label: "γ", latex: "\\gamma", category: "greek", tooltip: "gamma" },
  { label: "δ", latex: "\\delta", category: "greek", tooltip: "delta" },
  { label: "ε", latex: "\\epsilon", category: "greek", tooltip: "epsilon" },
  { label: "ζ", latex: "\\zeta", category: "greek", tooltip: "zeta" },
  { label: "η", latex: "\\eta", category: "greek", tooltip: "eta" },
  { label: "θ", latex: "\\theta", category: "greek", tooltip: "theta" },
  { label: "ι", latex: "\\iota", category: "greek", tooltip: "iota" },
  { label: "κ", latex: "\\kappa", category: "greek", tooltip: "kappa" },
  { label: "λ", latex: "\\lambda", category: "greek", tooltip: "lambda" },
  { label: "μ", latex: "\\mu", category: "greek", tooltip: "mu" },
  { label: "ν", latex: "\\nu", category: "greek", tooltip: "nu" },
  { label: "ξ", latex: "\\xi", category: "greek", tooltip: "xi" },
  { label: "π", latex: "\\pi", category: "greek", tooltip: "pi" },
  { label: "ρ", latex: "\\rho", category: "greek", tooltip: "rho" },
  { label: "σ", latex: "\\sigma", category: "greek", tooltip: "sigma" },
  { label: "τ", latex: "\\tau", category: "greek", tooltip: "tau" },
  { label: "υ", latex: "\\upsilon", category: "greek", tooltip: "upsilon" },
  { label: "φ", latex: "\\phi", category: "greek", tooltip: "phi" },
  { label: "χ", latex: "\\chi", category: "greek", tooltip: "chi" },
  { label: "ψ", latex: "\\psi", category: "greek", tooltip: "psi" },
  { label: "ω", latex: "\\omega", category: "greek", tooltip: "omega" },

  // Greek Letters - Uppercase
  { label: "Γ", latex: "\\Gamma", category: "greek", tooltip: "Gamma" },
  { label: "Δ", latex: "\\Delta", category: "greek", tooltip: "Delta" },
  { label: "Θ", latex: "\\Theta", category: "greek", tooltip: "Theta" },
  { label: "Λ", latex: "\\Lambda", category: "greek", tooltip: "Lambda" },
  { label: "Ξ", latex: "\\Xi", category: "greek", tooltip: "Xi" },
  { label: "Π", latex: "\\Pi", category: "greek", tooltip: "Pi" },
  { label: "Σ", latex: "\\Sigma", category: "greek", tooltip: "Sigma" },
  { label: "Φ", latex: "\\Phi", category: "greek", tooltip: "Phi" },
  { label: "Ψ", latex: "\\Psi", category: "greek", tooltip: "Psi" },
  { label: "Ω", latex: "\\Omega", category: "greek", tooltip: "Omega" },

  // Physics - Bra-ket
  { label: "⟨ψ|", latex: "\\bra{▢}", category: "physics", tooltip: "Bra" },
  { label: "|ψ⟩", latex: "\\ket{▢}", category: "physics", tooltip: "Ket" },
  { label: "⟨ψ|φ⟩", latex: "\\braket{▢}{▢}", category: "physics", tooltip: "Braket (inner product)" },
  { label: "⟨A⟩", latex: "\\ev{▢}", category: "physics", tooltip: "Expectation value" },
  { label: "⟨ψ|A|φ⟩", latex: "\\mel{▢}{▢}{▢}", category: "physics", tooltip: "Matrix element" },

  // Physics - Commutators
  { label: "[A,B]", latex: "\\comm{▢}{▢}", category: "physics", tooltip: "Commutator" },
  { label: "{A,B}", latex: "\\acomm{▢}{▢}", category: "physics", tooltip: "Anti-commutator" },

  // Physics - Derivatives
  { label: "d/dx", latex: "\\dv{▢}{▢}", category: "physics", tooltip: "Ordinary derivative" },
  { label: "∂/∂x", latex: "\\pdv{▢}{▢}", category: "physics", tooltip: "Partial derivative" },
  { label: "δ/δx", latex: "\\fdv{▢}{▢}", category: "physics", tooltip: "Functional derivative" },
  { label: "dx", latex: "\\dd{▢}", category: "physics", tooltip: "Differential" },

  // Physics - Vector calculus
  { label: "∇", latex: "\\grad", category: "physics", tooltip: "Gradient" },
  { label: "∇·", latex: "\\divergence", category: "physics", tooltip: "Divergence" },
  { label: "∇×", latex: "\\curl", category: "physics", tooltip: "Curl" },
  { label: "∇²", latex: "\\laplacian", category: "physics", tooltip: "Laplacian" },

  // Physics - Vectors
  { label: "𝐯", latex: "\\vb{▢}", category: "physics", tooltip: "Bold vector" },
  { label: "v̂", latex: "\\vu{▢}", category: "physics", tooltip: "Unit vector" },
  { label: "Â", latex: "\\op{▢}", category: "physics", tooltip: "Operator" },

  // Physics - Constants
  { label: "ℏ", latex: "\\hbar", category: "physics", tooltip: "h-bar" },

  // Chemistry - Basic formulas
  { label: "H₂O", latex: "\\ce{▢}", category: "chemistry", tooltip: "Chemical formula" },
  { label: "→", latex: "\\ce{▢ -> ▢}", category: "chemistry", tooltip: "Reaction arrow" },
  { label: "⇌", latex: "\\ce{▢ <=> ▢}", category: "chemistry", tooltip: "Equilibrium" },
  { label: "↑", latex: "\\ce{▢ ^}", category: "chemistry", tooltip: "Gas evolution" },
  { label: "↓", latex: "\\ce{▢ v}", category: "chemistry", tooltip: "Precipitation" },
  { label: "¹⁴C", latex: "\\ce{^{▢}_{▢}▢}", category: "chemistry", tooltip: "Isotope notation" },
  { label: "−", latex: "\\ce{▢-}", category: "chemistry", tooltip: "Single bond" },
  { label: "=", latex: "\\ce{▢=▢}", category: "chemistry", tooltip: "Double bond" },
  { label: "≡", latex: "\\ce{▢#▢}", category: "chemistry", tooltip: "Triple bond" },
  { label: "[aq]", latex: "\\ce{▢(aq)}", category: "chemistry", tooltip: "Aqueous" },
  { label: "[s]", latex: "\\ce{▢(s)}", category: "chemistry", tooltip: "Solid" },
  { label: "[l]", latex: "\\ce{▢(l)}", category: "chemistry", tooltip: "Liquid" },
  { label: "[g]", latex: "\\ce{▢(g)}", category: "chemistry", tooltip: "Gas" },

  // Symbols - Arrows
  { label: "→", latex: "\\rightarrow", category: "symbols", tooltip: "Right arrow" },
  { label: "←", latex: "\\leftarrow", category: "symbols", tooltip: "Left arrow" },
  { label: "↔", latex: "\\leftrightarrow", category: "symbols", tooltip: "Double arrow" },
  { label: "⇒", latex: "\\Rightarrow", category: "symbols", tooltip: "Implies" },
  { label: "⇐", latex: "\\Leftarrow", category: "symbols", tooltip: "Implied by" },
  { label: "⇔", latex: "\\Leftrightarrow", category: "symbols", tooltip: "If and only if" },
  { label: "↦", latex: "\\mapsto", category: "symbols", tooltip: "Maps to" },

  // Symbols - Relations
  { label: "≤", latex: "\\leq", category: "symbols", tooltip: "Less than or equal" },
  { label: "≥", latex: "\\geq", category: "symbols", tooltip: "Greater than or equal" },
  { label: "≠", latex: "\\neq", category: "symbols", tooltip: "Not equal" },
  { label: "≈", latex: "\\approx", category: "symbols", tooltip: "Approximately" },
  { label: "∼", latex: "\\sim", category: "symbols", tooltip: "Similar" },
  { label: "≡", latex: "\\equiv", category: "symbols", tooltip: "Equivalent" },
  { label: "∝", latex: "\\propto", category: "symbols", tooltip: "Proportional to" },
  { label: "≪", latex: "\\ll", category: "symbols", tooltip: "Much less than" },
  { label: "≫", latex: "\\gg", category: "symbols", tooltip: "Much greater than" },

  // Symbols - Sets
  { label: "∈", latex: "\\in", category: "symbols", tooltip: "Element of" },
  { label: "∉", latex: "\\notin", category: "symbols", tooltip: "Not element of" },
  { label: "⊂", latex: "\\subset", category: "symbols", tooltip: "Subset" },
  { label: "⊃", latex: "\\supset", category: "symbols", tooltip: "Superset" },
  { label: "⊆", latex: "\\subseteq", category: "symbols", tooltip: "Subset or equal" },
  { label: "∪", latex: "\\cup", category: "symbols", tooltip: "Union" },
  { label: "∩", latex: "\\cap", category: "symbols", tooltip: "Intersection" },
  { label: "∅", latex: "\\emptyset", category: "symbols", tooltip: "Empty set" },
  { label: "ℕ", latex: "\\mathbb{N}", category: "symbols", tooltip: "Natural numbers" },
  { label: "ℤ", latex: "\\mathbb{Z}", category: "symbols", tooltip: "Integers" },
  { label: "ℚ", latex: "\\mathbb{Q}", category: "symbols", tooltip: "Rationals" },
  { label: "ℝ", latex: "\\mathbb{R}", category: "symbols", tooltip: "Reals" },
  { label: "ℂ", latex: "\\mathbb{C}", category: "symbols", tooltip: "Complex numbers" },

  // Symbols - Logic
  { label: "∀", latex: "\\forall", category: "symbols", tooltip: "For all" },
  { label: "∃", latex: "\\exists", category: "symbols", tooltip: "There exists" },
  { label: "¬", latex: "\\neg", category: "symbols", tooltip: "Not" },
  { label: "∧", latex: "\\land", category: "symbols", tooltip: "And" },
  { label: "∨", latex: "\\lor", category: "symbols", tooltip: "Or" },

  // Symbols - Other
  { label: "∞", latex: "\\infty", category: "symbols", tooltip: "Infinity" },
  { label: "∂", latex: "\\partial", category: "symbols", tooltip: "Partial" },
  { label: "†", latex: "\\dagger", category: "symbols", tooltip: "Dagger" },
  { label: "°", latex: "^{\\circ}", category: "symbols", tooltip: "Degree" },
  { label: "±", latex: "\\pm", category: "symbols", tooltip: "Plus or minus" },
  { label: "∓", latex: "\\mp", category: "symbols", tooltip: "Minus or plus" },
  { label: "·", latex: "\\cdot", category: "symbols", tooltip: "Dot product" },
  { label: "×", latex: "\\times", category: "symbols", tooltip: "Cross product" },

  // Operators
  { label: "sin", latex: "\\sin", category: "operators", tooltip: "Sine" },
  { label: "cos", latex: "\\cos", category: "operators", tooltip: "Cosine" },
  { label: "tan", latex: "\\tan", category: "operators", tooltip: "Tangent" },
  { label: "ln", latex: "\\ln", category: "operators", tooltip: "Natural log" },
  { label: "log", latex: "\\log", category: "operators", tooltip: "Logarithm" },
  { label: "exp", latex: "\\exp", category: "operators", tooltip: "Exponential" },
  { label: "det", latex: "\\det", category: "operators", tooltip: "Determinant" },
  { label: "min", latex: "\\min", category: "operators", tooltip: "Minimum" },
  { label: "max", latex: "\\max", category: "operators", tooltip: "Maximum" },
  { label: "sup", latex: "\\sup", category: "operators", tooltip: "Supremum" },
  { label: "inf", latex: "\\inf", category: "operators", tooltip: "Infimum" },
  { label: "arg", latex: "\\arg", category: "operators", tooltip: "Argument" },
];

/**
 * Get symbols by category
 */
export function getSymbolsByCategory(category: SymbolCategory): SymbolButton[] {
  return EQUATION_SYMBOLS.filter((symbol) => symbol.category === category);
}

/**
 * Get all available categories
 */
export function getCategories(): SymbolCategory[] {
  return ["math", "greek", "physics", "chemistry", "symbols", "operators"];
}
